'use client'

import classnames from 'classnames'
import styles from './FeatureSpotlight.module.scss'
import FeatureSpotlightItem from '@/sections/FeatureSpotlight/FeatureSpotlightItem/FeatureSpotlightItem'
import 'swiper/css'
import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types'
import { useEffect, useRef, useState } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import Widont from '@/components/Widont/Widont'
import AnimateContainer from '@/components/AnimateContainer/AnimateContainer'
import Icon from '@/components/Icon/Icon'
import { getMenuItemListingGroupId } from '@/utils/urls'

export const FEATURE_SPOTLIGHT_NAV_ID = getMenuItemListingGroupId('spotlight')

const SPOTLIGHT_SCROLL_CTA_EVENT = 'menu_spotlight_scrollCTA'

const hasLifestyleImage = (dish: SanityPage) => Boolean(dish?.menuItemData?.lifestyleImages?.length)

export const getVisibleSpotlightDishes = (dishes: SanityFeatureSpotlight['dishes'] | undefined) =>
  (dishes ?? []).filter(hasLifestyleImage)

export const hasRenderableFeatureSpotlight = (section: SanityFeatureSpotlight | undefined) =>
  Boolean(section?.headline) && getVisibleSpotlightDishes(section?.dishes).length >= 2

const FeatureSpotlight = ({ className, headline, dishes }: SanityFeatureSpotlight) => {
  const carousel = useRef<Swiper | null>(null)
  const carouselRef = useRef<HTMLDivElement | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const resizeKey = useWindowResize()
  const [navState, setNavState] = useState({ isBeginning: true, isEnd: true })

  const visibleDishes = getVisibleSpotlightDishes(dishes)

  const updateNavState = (swiper: Swiper) => {
    setNavState({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd })
  }

  // Vertically centre the nav arrows on the (square) dish image rather than the
  // full card, which also contains the title/description below the image.
  const setArrowOffset = () => {
    const slide = carouselRef.current?.querySelector<HTMLElement>('.swiper-slide')
    if (slide && wrapperRef.current) {
      wrapperRef.current.style.setProperty('--spotlight-arrow-top', `${slide.clientWidth / 2}px`)
    }
  }

  const goToPrev = () => carousel.current?.slidePrev()
  const goToNext = () => carousel.current?.slideNext()

  useEffect(() => {
    if (!carouselRef.current) return

    if (carousel.current) {
      carousel.current.destroy()
    }

    const settings: SwiperOptions = {
      slidesPerView: 2.15,
      spaceBetween: 12,
      breakpoints: {
        768: {
          slidesPerView: 2.5,
          spaceBetween: 12,
        },
        1200: {
          slidesPerView: 3,
          spaceBetween: 12,
        },
      },
      on: {
        init: (swiper) => {
          updateNavState(swiper)
          setArrowOffset()
        },
        slideChange: updateNavState,
        resize: (swiper) => {
          updateNavState(swiper)
          setArrowOffset()
        },
      },
    }

    carousel.current = new Swiper(carouselRef.current, settings)
    setArrowOffset()

    return () => {
      if (carousel.current) {
        carousel.current.destroy()
      }
    }
  }, [resizeKey, visibleDishes.length])

  if (!headline || visibleDishes.length < 2) return null

  return (
    <section
      id={FEATURE_SPOTLIGHT_NAV_ID}
      data-menu-category="spotlight"
      data-scroll-header-offset="true"
      className={classnames(styles.FeatureSpotlight, className)}
    >
      <AnimateContainer
        animationType="fadeIn"
        className={styles.inner}
      >
        <h2 className={styles.title}>
          <Widont text={headline} />
        </h2>
        <div
          className={styles.carouselWrapper}
          ref={wrapperRef}
        >
          <button
            type="button"
            className={classnames(styles.navArrow, styles.navArrowPrev, {
              [styles.navArrowHidden]: navState.isBeginning,
            })}
            onClick={goToPrev}
            disabled={navState.isBeginning}
            aria-label="Previous dishes"
            data-gtm-event-name={SPOTLIGHT_SCROLL_CTA_EVENT}
          >
            <Icon
              name="caretRight"
              className={styles.navArrowIcon}
            />
          </button>
          <div
            className={styles.carouselContainer}
            ref={carouselRef}
          >
            <ul className={classnames('swiper-wrapper', styles.listWrapper)}>
              {visibleDishes.map((dish, i) => (
                <li
                  key={`${dish.slug?.current ?? 'dish'}-${i}`}
                  className={classnames('swiper-slide', styles.listWrapper__item)}
                >
                  <FeatureSpotlightItem
                    dish={dish}
                    className={styles.listWrapper__itemComponent}
                  />
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            className={classnames(styles.navArrow, styles.navArrowNext, {
              [styles.navArrowHidden]: navState.isEnd,
            })}
            onClick={goToNext}
            disabled={navState.isEnd}
            aria-label="Next dishes"
            data-gtm-event-name={SPOTLIGHT_SCROLL_CTA_EVENT}
          >
            <Icon
              name="caretRight"
              className={styles.navArrowIcon}
            />
          </button>
        </div>
      </AnimateContainer>
    </section>
  )
}

FeatureSpotlight.displayName = 'FeatureSpotlight'

export default FeatureSpotlight
